import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { GET_JOBS_FOR_ASSIGNMENTS, GET_ASSIGNED_JOBS_FOR_ASSIGNMENTS, GET_USERS, GET_ELIGIBLE_USERS_FOR_JOBS } from '../graphql/queries';
import { ASSIGN_JOBS, ASSIGN_JOBS_TO_TEAM, BULK_UPDATE_JOB_DATES, BULK_UPDATE_AND_ASSIGN_JOBS } from '../graphql/mutations';
import { GET_ACTIVE_TEAMS } from '../graphql/teams';
import { format, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import EditJobModal from '../components/EditJobModal';
import AddJobModal from '../components/AddJobModal';
import AssignmentsHeader from '../components/assignments/AssignmentsHeader';
import AssignedTasks from '../components/assignments/AssignedTasks';
import AssignedTasksOptimized from '../components/assignments/AssignedTasksOptimized';
import UnassignedTasks from '../components/assignments/UnassignedTasks';
import UnassignedTasksInfiniteScroll from '../components/assignments/UnassignedTasksInfiniteScroll';
import AutoLoader from '../components/assignments/AutoLoader';
import { useAuth } from '../contexts/AuthContext';
import { useAssignmentsInfiniteScroll } from '../hooks/useAssignmentsInfiniteScroll';

import { toast } from 'react-hot-toast';
import { API_PAGE_LIMITS, DISPLAY_LIMITS } from '../utils/constants';
import { useServiceTypes } from '../hooks/useServiceTypes';
import { useFilterBarState } from '../hooks/useFilterBarState';
import Spinner from '../components/Spinner';

// Import utility functions
import { 
  formatJobCategory, 
  formatTime, 
  isOwnerArrival, 
  getCheckoutTime, 
  isLateCheckout, 
  getBorderColor, 
  isManualJob,
  formatJobDate
} from '../utils/job-utils';

// Import user filtering utilities
import { 
  filterUsersByAreasAndServiceTypes, 
  getFilteringInfo 
} from '../utils/user-filtering-utils';

// Import UI components
import {
  FlipIndicator,
  WalkthroughIndicator,
  LateCheckoutIndicator,
  CustomJobIndicator,
  DateOverrideIndicator,
  OwnerArrivalIndicator,
  OwnerDepartureIndicator,
  JobCategoryPill,
  AreaIndicator
} from '../components/job-indicators';

// Import state management utilities
import {
  updateJobInLocalState,
  checkAndFixJobData,
  handleAddNewJob,
  handleAddNewJobWithCheck,
  getFormattedDateRange
} from '../utils/job-state-utils';

// Import cache invalidation utilities
import { invalidateAssignmentCaches, invalidateAssignmentCachesForDateChange } from '../utils/cache-utils';

// Import job card components
import JobCardUnassigned from '../components/schedule/job-cards/JobCardUnassigned';
import JobCardAssigned from '../components/schedule/job-cards/JobCardAssigned';

// 🎯 PERFORMANCE OPTIMIZATION: Optimized limits for better UX
const PERFORMANCE_LIMITS = {
  INITIAL_JOBS: 20,        // Standard 20 jobs like Schedule.js
  ASSIGNED_JOBS: 25,       // Reduced assigned jobs limit
  USERS: 20,              // Reduced users limit
  PROGRESSIVE_LOAD: 20,    // Load 20 more jobs at a time (consistent with Schedule.js)
  MAX_RETRIES: 3,         // Maximum retry attempts for failed queries
  TIMEOUT_DELAY: 5000,    // 5 second timeout for queries
  PROGRESSIVE_DELAY: 1000  // 1 second delay between progressive loads
};

// Helper to format area name: remove underscores and capitalize each word
const formatAreaName = (area) => {
  if (!area) return '';
  return area
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export default function Assignments() {
  const { user, loading: authLoading } = useAuth() || {};
  const client = useApolloClient();
  
  // Enhanced service type filtering with role-based auto-initialization
  const {
    selectedServiceTypes,
    setSelectedServiceTypes,
    isRoleFilterEnabled,
    setRoleFilterEnabled,
    filterInfo,
    loading: serviceTypesLoading,
    availableServiceTypes
  } = useServiceTypes({
    enableRoleFilter: true, // Enable role filter by default
    autoSelectFiltered: true // Auto-select role-appropriate service types
  });
  
  // Stabilize availableServiceTypes to prevent unnecessary re-renders
  const stableAvailableServiceTypes = useMemo(() => {
    return availableServiceTypes;
  }, [availableServiceTypes?.length, serviceTypesLoading]);

  // Use the filter bar state hook to isolate UI state from data state
  const {
    isFiltersOpen,
    propertySearch,
    showPropertyDropdown,
    hasUserInteractedWithAreas,
    setIsFiltersOpen,
    setPropertySearch,
    setShowPropertyDropdown,
    setHasUserInteractedWithAreas
  } = useFilterBarState();

  // Other filter states
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(['PENDING', 'IN_PROGRESS']);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
    return today;
  });
  const [dateRange, setDateRange] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArea, setSelectedArea] = useState('');

  const [editingJob, setEditingJob] = useState(null);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [displayLimit, setDisplayLimit] = useState(DISPLAY_LIMITS.JOBS);
  
  // Track displayLimit changes
  useEffect(() => {
    // DisplayLimit tracking for pagination management
  }, [displayLimit]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const unassignedJobsContainerRef = useRef(null);
  const [isAddJobModalOpen, setIsAddJobModalOpen] = useState(false);
  const [copyJobData, setCopyJobData] = useState(null);
  const [filters, setFilters] = useState({
    fromDate: format(new Date(), 'yyyy-MM-dd'),
    toDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    serviceType: '',
    propertyIds: []
  });
  
  // Add state for selected areas
  const [selectedAreas, setSelectedAreas] = useState([]);
  
  // Add state for date range filters - fix to use today's date
  const [startDate, setStartDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  
  // 🎯 ENHANCED: Better date synchronization to prevent inconsistencies
  useEffect(() => {
    // Only sync if we're in single day mode (startDate === endDate)
    if (startDate === endDate) {
      const startDateObj = new Date(startDate + 'T12:00:00');
      const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
      
      // If the dates are different, update selectedDate to match startDate
      if (startDate !== selectedDateStr) {
        setSelectedDate(startDateObj);
      }
    }
  }, [startDate, endDate, selectedDate]);

  // Create a stable reference for GraphQL variables to prevent unnecessary refetches
  const queryVariables = useMemo(() => {
    // Create stable arrays to prevent unnecessary re-renders
    const stableAreas = selectedAreas.length > 0 ? [...selectedAreas].sort() : undefined;
    const stableServiceTypes = selectedServiceTypes.length > 0 ? [...selectedServiceTypes].sort() : undefined;
    const stablePropertyIds = filters.propertyIds.length > 0 ? [...filters.propertyIds].sort() : undefined;
    
    // Use stable date formatting to prevent unnecessary re-computations
    const startDateFormatted = startDate ? 
      `${startDate}T00:00:00.000Z` 
      : 
      `${format(selectedDate, 'yyyy-MM-dd')}T00:00:00.000Z`;
    
    const endDateFormatted = endDate ? 
      `${endDate}T23:59:59.999Z` 
      : 
      `${format(selectedDate, 'yyyy-MM-dd')}T23:59:59.999Z`;
    
    return {
      input: {
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        areas: stableAreas,
        serviceTypes: stableServiceTypes,
        propertyIds: stablePropertyIds,
        onlyAssigned: false, // Only get unassigned jobs for the main query
        limit: PERFORMANCE_LIMITS.INITIAL_JOBS, // 🎯 REDUCED: Use performance-optimized limit
        offset: 0
      }
    };
  }, [
    startDate, 
    endDate, 
    selectedAreas.length, // Use length instead of array to prevent unnecessary re-renders
    selectedServiceTypes.length, // Use length instead of array to prevent unnecessary re-renders
    filters.propertyIds.length, // Use length instead of array to prevent unnecessary re-renders
    selectedDate.getTime() // Use timestamp for stable comparison
  ]);
  
  // Use query variables directly without debounce since they're now stable
  const debouncedQueryVariables = queryVariables;

  // 🎯 NEW: Additional effect to ensure query variables are regenerated when dates change
  useEffect(() => {
    // This effect ensures that when dates change, we force a re-computation of query variables
    // This helps prevent stale cache issues
  }, [selectedDate, startDate, endDate, queryVariables?.input?.startDate, queryVariables?.input?.endDate]);
  
  // Add state for department selection
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  
  // Add state to track user interactions with service types
  const [hasUserInteractedWithServiceTypes, setHasUserInteractedWithServiceTypes] = useState(false);
  
  // Add job counts state to track total, assigned, and unassigned jobs
  const [jobCounts, setJobCounts] = useState({
    total: 0,
    assigned: 0,
    unassigned: 0
  });

  // Add column layout state with localStorage persistence
  const [columnLayout, setColumnLayout] = useState(() => {
    const saved = localStorage.getItem('assignments-column-layout');
    return saved ? parseInt(saved) : 1;
  });

  // Add a state variable to track if we have loaded all server-side jobs
  const [allJobsLoaded, setAllJobsLoaded] = useState(false);
  
  // Add the needsSecondPage state
  const [needsSecondPage, setNeedsSecondPage] = useState(false);
  
  // Add initialization state to prevent multiple runs
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  
  // Enhanced loading state to prevent flickering
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [loadingStartTime] = useState(Date.now());
  const MINIMUM_LOADING_TIME = 200; // Reduced to 200ms for faster loading

  // 🎯 NEW: Loading state for date transitions
  const [isDateTransitioning, setIsDateTransitioning] = useState(false);

  // 🎯 NEW: Performance optimization states
  const [queryRetryCount, setQueryRetryCount] = useState(0);
  const [isProgressiveLoading, setIsProgressiveLoading] = useState(false);
  const [progressiveLoadQueue, setProgressiveLoadQueue] = useState([]);
  const [hasQueryError, setHasQueryError] = useState(false);
  const [lastSuccessfulLoad, setLastSuccessfulLoad] = useState(null);

  // Feature flag for optimized loading (can be controlled via environment or user preference)
  // Always use optimized assigned tasks loading for better performance
  const useOptimizedAssignedTasks = true;
  
  // Feature flag for infinite scroll unassigned tasks (can be controlled via environment or user preference)
  const useInfiniteScrollUnassigned = false; // Disable for now to fix jumping issue

  // Add cache clearing trigger for AssignedTasksOptimized
  const [assignedTasksCacheTrigger, setAssignedTasksCacheTrigger] = useState(0);

  // Save column layout preference to localStorage
  useEffect(() => {
    localStorage.setItem('assignments-column-layout', columnLayout.toString());
  }, [columnLayout]);

  // Get current user and role
  const isHousekeeperManager = user?.role === 'HOUSEKEEPER_MANAGER';
  const isPropertyManager = user?.role === 'PROPERTY_MANAGER';
  
  // Function to show a notification toast
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ show: true, message, type });
    // Auto-hide the notification after 3 seconds - using safe timeout
    const timeoutId = setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
    
    // Store timeout ID for cleanup
    return () => clearTimeout(timeoutId);
  }, []);



  // Render Toast Notification component
  const renderNotification = useCallback(() => {
    if (!notification.show) return null;

    const bgColor = notification.type === 'success' ? 'bg-green-50 border-green-500' : 
                   notification.type === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                   'bg-red-50 border-red-500';
    const textColor = notification.type === 'success' ? 'text-green-800' : 
                     notification.type === 'warning' ? 'text-yellow-800' :
                     'text-red-800';
    const iconColor = notification.type === 'success' ? 'text-green-500' : 
                     notification.type === 'warning' ? 'text-yellow-500' :
                     'text-red-500';
    
    return (
      <div className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg border-l-4 ${bgColor} transition-opacity duration-300`}>
        <div className="flex items-center">
          <div className={`mr-3 ${iconColor}`}>
            {notification.type === 'success' ? (
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : notification.type === 'warning' ? (
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <div className={`font-medium ${textColor}`}>
            {notification.message}
          </div>
        </div>
      </div>
    );
  }, [notification]);
  
  // 🎯 SIMPLE ERROR HANDLER: Basic error handling without circular dependencies
  const simpleErrorHandler = useCallback((error, queryName) => {
    console.error(`${queryName} error:`, error);
    
    // Check if it's a timeout error
    const isTimeoutError = error.message?.includes('504') || 
                          error.message?.includes('timeout') ||
                          error.message?.includes('Gateway Timeout');
    
    if (isTimeoutError) {
      setHasQueryError(true);
      setQueryRetryCount(prev => prev + 1);
      
      if (queryRetryCount < PERFORMANCE_LIMITS.MAX_RETRIES) {
        showNotification(`Loading ${queryName} is taking longer than expected. Retrying... (${queryRetryCount + 1}/${PERFORMANCE_LIMITS.MAX_RETRIES})`, 'warning');
        
        // Simple retry with page reload after delay - using safe timeout
        const timeoutId = setTimeout(() => {
          window.location.reload();
        }, PERFORMANCE_LIMITS.TIMEOUT_DELAY);
        
        // Store timeout ID for cleanup (though this will be cleared on page reload)
        return () => clearTimeout(timeoutId);
      } else {
        showNotification(`Unable to load ${queryName} due to server timeout. Please try again later.`, 'error');
      }
    } else {
      showNotification(`Error loading ${queryName}: ${error.message}`, 'error');
      setHasQueryError(true);
    }
  }, [showNotification, queryRetryCount]);

  // 🎯 PERFORMANCE OPTIMIZATION: Prioritize main jobs query with reduced initial limit
  const { 
    loading: jobsLoading, 
    error: jobsError, 
    data: jobsData, 
    refetch: refetchJobs,
    fetchMore
  } = useQuery(GET_JOBS_FOR_ASSIGNMENTS, {
    variables: {
      ...debouncedQueryVariables,
      input: {
        ...debouncedQueryVariables.input,
        limit: PERFORMANCE_LIMITS.INITIAL_JOBS // 🎯 REDUCED: Start with smaller limit
      }
    },
    skip: serviceTypesLoading || useInfiniteScrollUnassigned, // Skip if using infinite scroll
    fetchPolicy: 'cache-first', // Use cache-first for better performance
    errorPolicy: 'all', // Show partial data even if there are errors
    notifyOnNetworkStatusChange: false, // Disable to reduce re-renders
    returnPartialData: true, // Return partial data while loading
    // 🎯 NEW: Add result caching to prevent unnecessary re-renders
    nextFetchPolicy: 'cache-first',

    onError: (error) => {
      // Only log actual errors, not cancellations
      if (error.name !== 'AbortError' && !error.message.includes('cancelled')) {
        simpleErrorHandler(error, 'jobs');
      }
    }
  });
  
  // 🎯 NEW: Infinite scroll hook for unassigned tasks
  const infiniteScrollJobs = useAssignmentsInfiniteScroll({
    query: GET_JOBS_FOR_ASSIGNMENTS,
    variables: debouncedQueryVariables,
    pageSize: 20, // Use consistent page size
    enabled: !serviceTypesLoading && useInfiniteScrollUnassigned,
    onError: simpleErrorHandler
  });
  
  // 🎯 PERFORMANCE OPTIMIZATION: Delay secondary queries until main query completes
  const shouldDelaySecondaryQueries = useInfiniteScrollUnassigned 
    ? (infiniteScrollJobs.loading || serviceTypesLoading)
    : (jobsLoading || serviceTypesLoading);
  
  // Separate query for assigned jobs - delayed for better performance
  const assignedJobsQueryVariables = useMemo(() => {
    return {
      input: {
        ...debouncedQueryVariables.input,
        limit: PERFORMANCE_LIMITS.ASSIGNED_JOBS, // 🎯 REDUCED: Use performance-optimized limit
        offset: 0,
        onlyAssigned: true // Filter for only assigned jobs on the backend
      }
    };
  }, [debouncedQueryVariables]);

  const { 
    loading: assignedJobsLoading, 
    error: assignedJobsError, 
    data: assignedJobsData, 
    refetch: refetchAssignedJobs
  } = useQuery(GET_ASSIGNED_JOBS_FOR_ASSIGNMENTS, {
    variables: assignedJobsQueryVariables,
    skip: shouldDelaySecondaryQueries, // Wait for main query to complete
    fetchPolicy: 'cache-first', // Use cache-first for better performance
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: false, // Disable to reduce re-renders
    returnPartialData: true, // Return partial data while loading
    // 🎯 NEW: Add result caching to prevent unnecessary re-renders
    nextFetchPolicy: 'cache-first',

    onError: (error) => {
      if (error.name !== 'AbortError' && !error.message.includes('cancelled')) {
        simpleErrorHandler(error, 'assigned jobs');
      }
    }
  });
  
  // 🎯 PERFORMANCE OPTIMIZATION: Delay users and teams queries for faster initial load
  const { loading: usersLoading, error: usersError, data: usersData } = useQuery(GET_USERS, {
    skip: shouldDelaySecondaryQueries, // Wait for main query to complete
    fetchPolicy: 'cache-first', // Use cache first for better performance
    notifyOnNetworkStatusChange: false, // Disable to reduce re-renders
    returnPartialData: true, // Return partial data while loading

    onError: (error) => {
      if (error.name !== 'AbortError' && !error.message.includes('cancelled')) {
        simpleErrorHandler(error, 'users');
      }
    }
  });

  // Load teams data for team assignment display - delayed
  const { data: teamsData } = useQuery(GET_ACTIVE_TEAMS, {
    skip: shouldDelaySecondaryQueries, // Wait for main query to complete
    fetchPolicy: 'cache-first', // Use cache first for better performance
    notifyOnNetworkStatusChange: false // Disable to reduce re-renders
  });

  // 🎯 PERFORMANCE OPTIMIZATION: Only load eligible users when jobs are selected
  const { data: eligibleUsersData, loading: eligibleUsersLoading } = useQuery(GET_ELIGIBLE_USERS_FOR_JOBS, {
    variables: {
      input: { jobIds: selectedJobs }
    },
    skip: !selectedJobs || selectedJobs.length === 0 || shouldDelaySecondaryQueries,
    fetchPolicy: 'cache-first', // Use cache first for better performance
    notifyOnNetworkStatusChange: false, // Disable to reduce re-renders
    errorPolicy: 'all', // Show partial data even if there are errors
    onError: (error) => {
      // Only log actual errors, not cancellations
      if (error.name !== 'AbortError' && !error.message.includes('cancelled')) {
        simpleErrorHandler(error, 'eligible users');
      }
    }
  });

  // Mutations
  const [assignJobs, { loading: assignLoading }] = useMutation(ASSIGN_JOBS, {
    onCompleted: async (data) => {
      const assignedJobCount = selectedJobs.length;
      
      // Show success message
      toast.success(`Successfully assigned ${assignedJobCount} ${assignedJobCount === 1 ? 'job' : 'jobs'}`);
      
      // Clear selected jobs
      setSelectedJobs([]);
      
      // Comprehensive cache invalidation
      client.cache.evict({ fieldName: 'jobsForAssignments' });
      client.cache.evict({ fieldName: 'assignedJobsForAssignments' });
      client.cache.evict({ fieldName: 'jobs' });
      
      // Clear optimized assignment caches if using optimized mode
      if (useOptimizedAssignedTasks) {
        client.cache.evict({ fieldName: 'assignedEmployeesSummary' });
        client.cache.evict({ fieldName: 'employeeAssignedJobs' });
      }
      
      client.cache.gc();
      
      // Force refetch both queries to ensure fresh data
      if (useInfiniteScrollUnassigned) {
        // Use infinite scroll refetch and auto-backfill
        await infiniteScrollJobs.refetchAll();
        refetchAssignedJobs();
        
        // Trigger assigned tasks cache clearing
        setAssignedTasksCacheTrigger(prev => prev + 1);
        
        // Auto-backfill using infinite scroll method
        const timeoutId1 = setTimeout(() => {
          infiniteScrollJobs.autoBackfillIfNeeded(assignedJobCount);
        }, 300);
        
        return () => clearTimeout(timeoutId1);
      } else {
        // Use traditional refetch method
        const refetchResult = await refetchJobs();
        refetchAssignedJobs();
        
        // Trigger assigned tasks cache clearing
        setAssignedTasksCacheTrigger(prev => prev + 1);
        
        // Auto-backfill: If we have fewer unassigned jobs than expected, load more
        const timeoutId1 = setTimeout(() => {
          const allItems = refetchResult?.data?.jobsForAssignments?.items || [];
          const currentUnassignedCount = allItems.filter(job => !job.assignedTo?.id && !job.assignedTeamId).length;
          const hasMore = refetchResult?.data?.jobsForAssignments?.hasMore;
          const MIN_VISIBLE_JOBS = 10; // Keep minimum jobs visible
          
          if (currentUnassignedCount < MIN_VISIBLE_JOBS && hasMore && !isLoadingMore) {
            const jobsToLoad = Math.max(assignedJobCount, 20); // Load enough jobs to replace assigned ones
            loadMoreJobs(jobsToLoad);
          } else if (currentUnassignedCount === 0 && hasMore && !isLoadingMore) {
            // Special case: if no jobs visible at all, always load some
            loadMoreJobs(20);
          }
        }, 300); // Reduced delay for faster response
        
        // Store timeout ID for cleanup
        return () => clearTimeout(timeoutId1);
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to assign jobs');
    }
  });

  const [assignJobsToTeam, { loading: assignToTeamLoading }] = useMutation(ASSIGN_JOBS_TO_TEAM, {
    onCompleted: async (data) => {
      const assignedJobCount = selectedJobs.length;
      
      // Show success message
      toast.success(`Successfully assigned ${assignedJobCount} ${assignedJobCount === 1 ? 'job' : 'jobs'} to team`);
      
      // Clear selected jobs
      setSelectedJobs([]);
      
      // Comprehensive cache invalidation
      client.cache.evict({ fieldName: 'jobsForAssignments' });
      client.cache.evict({ fieldName: 'assignedJobsForAssignments' });
      client.cache.evict({ fieldName: 'jobs' });
      
      // Clear optimized assignment caches if using optimized mode
      if (useOptimizedAssignedTasks) {
        client.cache.evict({ fieldName: 'assignedEmployeesSummary' });
        client.cache.evict({ fieldName: 'employeeAssignedJobs' });
      }
      
      client.cache.gc();
      
      // Force refetch both queries to ensure fresh data
      if (useInfiniteScrollUnassigned) {
        // Use infinite scroll refetch and auto-backfill
        await infiniteScrollJobs.refetchAll();
        refetchAssignedJobs();
        
        // Trigger assigned tasks cache clearing  
        setAssignedTasksCacheTrigger(prev => prev + 1);
        
        // Auto-backfill using infinite scroll method
        const timeoutId2 = setTimeout(() => {
          infiniteScrollJobs.autoBackfillIfNeeded(assignedJobCount);
        }, 300);
        
        return () => clearTimeout(timeoutId2);
      } else {
        // Use traditional refetch method
        const refetchResult = await refetchJobs();
        refetchAssignedJobs();
        
        // Trigger assigned tasks cache clearing  
        setAssignedTasksCacheTrigger(prev => prev + 1);
        
        // Auto-backfill: If we have fewer unassigned jobs than expected, load more
        const timeoutId2 = setTimeout(() => {
          const allItems = refetchResult?.data?.jobsForAssignments?.items || [];
          const currentUnassignedCount = allItems.filter(job => !job.assignedTo?.id && !job.assignedTeamId).length;
          const hasMore = refetchResult?.data?.jobsForAssignments?.hasMore;
          const MIN_VISIBLE_JOBS = 10; // Keep minimum jobs visible
          
          if (currentUnassignedCount < MIN_VISIBLE_JOBS && hasMore && !isLoadingMore) {
            const jobsToLoad = Math.max(assignedJobCount, 20); // Load enough jobs to replace assigned ones
            loadMoreJobs(jobsToLoad);
          } else if (currentUnassignedCount === 0 && hasMore && !isLoadingMore) {
            // Special case: if no jobs visible at all, always load some
            loadMoreJobs(20);
          }
        }, 300); // Reduced delay for faster response
        
        // Store timeout ID for cleanup
        return () => clearTimeout(timeoutId2);
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to assign jobs to team');
    }
  });

  // 🎯 NEW: Bulk date update mutation
  const [bulkUpdateJobDates, { loading: bulkDateUpdateLoading }] = useMutation(BULK_UPDATE_JOB_DATES, {
    onCompleted: (data) => {
      const { success, updatedCount, errors } = data.bulkUpdateJobDates;
      
      if (success) {
        toast.success(`Successfully updated dates for ${updatedCount} ${updatedCount === 1 ? 'job' : 'jobs'}`);
      } else if (errors.length > 0) {
        toast.error(`Bulk date update completed with errors: ${errors.slice(0, 2).join(', ')}`);
      }
      
      // Clear selected jobs after date change for safety
      setSelectedJobs([]);
      
      // Comprehensive cache invalidation
      client.cache.evict({ fieldName: 'jobsForAssignments' });
      client.cache.evict({ fieldName: 'assignedJobsForAssignments' });
      client.cache.evict({ fieldName: 'jobs' });
      
      // Clear optimized assignment caches if using optimized mode
      if (useOptimizedAssignedTasks) {
        client.cache.evict({ fieldName: 'assignedEmployeesSummary' });
        client.cache.evict({ fieldName: 'employeeAssignedJobs' });
      }
      
      client.cache.gc();
      
      // Force refetch both queries to ensure fresh data
      if (useInfiniteScrollUnassigned) {
        infiniteScrollJobs.refetchAll();
        refetchAssignedJobs();
      } else {
        refetchJobs();
        refetchAssignedJobs();
      }
      
      // Trigger assigned tasks cache clearing  
      setAssignedTasksCacheTrigger(prev => prev + 1);
      
    },
    onError: (error) => {
      console.error('Bulk date update error:', error);
      toast.error(`Failed to update job dates: ${error.message}`);
    }
  });

  // 🎯 NEW: Combined bulk update and assign mutation
  const [bulkUpdateAndAssignJobs, { loading: bulkUpdateAndAssignLoading }] = useMutation(BULK_UPDATE_AND_ASSIGN_JOBS, {
    onCompleted: (data) => {
      const { success, updatedCount, assignedCount, errors, summary } = data.bulkUpdateAndAssignJobs;
      
      if (success) {
        toast.success(summary || `Successfully updated ${updatedCount} jobs and assigned ${assignedCount} jobs`);
      } else if (errors.length > 0) {
        toast.error(`Bulk operation completed with errors: ${errors.slice(0, 2).join(', ')}`);
      }
      
      // Clear selected jobs after successful combined operation
      setSelectedJobs([]);
      
      // Comprehensive cache invalidation
      client.cache.evict({ fieldName: 'jobsForAssignments' });
      client.cache.evict({ fieldName: 'assignedJobsForAssignments' });
      client.cache.evict({ fieldName: 'jobs' });
      
      // Clear optimized assignment caches if using optimized mode
      if (useOptimizedAssignedTasks) {
        client.cache.evict({ fieldName: 'assignedEmployeesSummary' });
        client.cache.evict({ fieldName: 'employeeAssignedJobs' });
      }
      
      client.cache.gc();
      
      // Force refetch both queries to ensure fresh data
      if (useInfiniteScrollUnassigned) {
        infiniteScrollJobs.refetchAll();
        refetchAssignedJobs();
      } else {
        refetchJobs();
        refetchAssignedJobs();
      }
      
      // Trigger assigned tasks cache clearing  
      setAssignedTasksCacheTrigger(prev => prev + 1);
      
    },
    onError: (error) => {
      console.error('Bulk update and assign error:', error);
      toast.error(`Failed to update and assign jobs: ${error.message}`);
    }
  });

  // 🎯 NEW: Functions that depend on query results (defined after queries to avoid circular dependencies)
  
  // 🎯 NEW: Progressive loading function
  const loadJobsProgressively = useCallback(async () => {
    if (isProgressiveLoading || !jobsData?.jobsForAssignments?.hasMore) {
      return;
    }
    
    setIsProgressiveLoading(true);
    
    try {
      // Load next batch of jobs
      const currentOffset = jobsData.jobsForAssignments.items.length;
      
      const result = await fetchMore({
        variables: {
          ...debouncedQueryVariables,
          input: {
            ...debouncedQueryVariables.input,
            offset: currentOffset,
            limit: PERFORMANCE_LIMITS.PROGRESSIVE_LOAD
          }
        }
      });
      
      if (result.data?.jobsForAssignments?.items?.length) {
        setLastSuccessfulLoad(Date.now());
        setQueryRetryCount(0); // Reset retry count on success
        setHasQueryError(false);
        
        // Update display limit
        setDisplayLimit(prev => prev + result.data.jobsForAssignments.items.length);
        
        // Check if we need to load more
        if (result.data.jobsForAssignments.hasMore && 
            result.data.jobsForAssignments.items.length < PERFORMANCE_LIMITS.PROGRESSIVE_LOAD) {
          // Queue next load
          const timeoutId3 = setTimeout(() => {
            loadJobsProgressively();
          }, PERFORMANCE_LIMITS.PROGRESSIVE_DELAY);
          
          // Store timeout ID for cleanup
          return () => clearTimeout(timeoutId3);
        }
      }
    } catch (error) {
      simpleErrorHandler(error, 'progressive jobs');
    } finally {
      setIsProgressiveLoading(false);
    }
  }, [isProgressiveLoading, jobsData, debouncedQueryVariables, fetchMore, simpleErrorHandler]);

  // 🎯 NEW: Refetch with reduced limits
  const refetchJobsWithReducedLimits = useCallback(async () => {
    const reducedVariables = {
      ...debouncedQueryVariables,
      input: {
        ...debouncedQueryVariables.input,
        limit: Math.max(5, Math.floor(PERFORMANCE_LIMITS.INITIAL_JOBS / 2)) // Reduce limit by half
      }
    };
    
    try {
      await refetchJobs(reducedVariables);
      setLastSuccessfulLoad(Date.now());
      setQueryRetryCount(0);
      setHasQueryError(false);
    } catch (error) {
      simpleErrorHandler(error, 'jobs');
    }
  }, [debouncedQueryVariables, refetchJobs, simpleErrorHandler]);

  const refetchAssignedJobsWithReducedLimits = useCallback(async () => {
    const reducedVariables = {
      ...assignedJobsQueryVariables,
      input: {
        ...assignedJobsQueryVariables.input,
        limit: Math.max(10, Math.floor(PERFORMANCE_LIMITS.ASSIGNED_JOBS / 2))
      }
    };
    
    try {
      await refetchAssignedJobs(reducedVariables);
      setLastSuccessfulLoad(Date.now());
      setQueryRetryCount(0);
      setHasQueryError(false);
    } catch (error) {
      simpleErrorHandler(error, 'assigned jobs');
    }
  }, [assignedJobsQueryVariables, refetchAssignedJobs, simpleErrorHandler]);



  // Computed values
  const groupedAssignedJobs = useMemo(() => {
    
    if (!assignedJobsData?.jobsForAssignments?.items) return [];
    
    const grouped = {};
    assignedJobsData.jobsForAssignments.items.forEach(job => {
      const isTeamAssignment = !!job.assignedTeamId;
      
      if (isTeamAssignment) {
        // For team assignments, group by team
        const teamId = job.assignedTeamId;
        if (!grouped[`team-${teamId}`]) {
          grouped[`team-${teamId}`] = {
            employee: {
              id: `team-${teamId}`,
              name: job.assignedTeam?.name || `Team ${teamId}`,
              role: 'TEAM',
              // Add team-specific properties for display
              isTeam: true,
              teamId: teamId,
              teamColor: job.assignedTeam?.color,
              memberCount: job.assignedTeam?.memberCount || 0
            },
            jobs: [],
            isTeamAssignment: true,
            teamId: teamId
          };
        }
        grouped[`team-${teamId}`].jobs.push(job);
      } else {
        // For individual assignments, group by user
        const assignedToId = job.assignedTo?.id;
        if (!grouped[`user-${assignedToId}`]) {
          grouped[`user-${assignedToId}`] = {
            employee: job.assignedTo, // Use 'employee' to match component expectations
            jobs: [],
            isTeamAssignment: false,
            teamId: null
          };
        }
        grouped[`user-${assignedToId}`].jobs.push(job);
      }
    });
    
    // Convert to array and sort alphabetically by name
    return Object.values(grouped).sort((a, b) => {
      const nameA = a.employee?.name || '';
      const nameB = b.employee?.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [assignedJobsData?.jobsForAssignments?.items]);

  const totalJobsCount = useMemo(() => {
    if (useInfiniteScrollUnassigned) {
      // Use infinite scroll data
      return infiniteScrollJobs.jobCounts;
    } else {
      // Use traditional query data - prioritize backend counts over client calculations
      const allItems = jobsData?.jobsForAssignments?.items || [];
      const assignedJobs = assignedJobsData?.assignedJobsForAssignments?.items || [];
      
      // Use backend counts if available, otherwise calculate from loaded items
      const total = jobsData?.jobsForAssignments?.total || allItems.length;
      const assigned = jobsData?.jobsForAssignments?.assigned || assignedJobs.length;
      const unassigned = jobsData?.jobsForAssignments?.unassigned || allItems.filter(job => !job.assignedTo?.id && !job.assignedTeamId).length;
      
      
      return {
        total,
        assigned,
        unassigned
      };
    }
  }, [
    useInfiniteScrollUnassigned,
    infiniteScrollJobs.jobCounts,
    jobsData?.jobsForAssignments?.items?.length,
    jobsData?.jobsForAssignments?.total,
    jobsData?.jobsForAssignments?.hasMore,
    assignedJobsData?.assignedJobsForAssignments?.items?.length,
    // 🎯 NEW: Add more specific dependencies to prevent unnecessary recalculations
    jobsData?.jobsForAssignments?.items?.map(job => job.id).join(','),
    assignedJobsData?.assignedJobsForAssignments?.items?.map(job => job.id).join(',')
  ]);

  const filteredUsers = useMemo(() => {
    if (!usersData?.users) return [];
    
    // Determine department mode
    const isDepartmentHousekeeping = selectedDepartment === 'housekeeping';
    const isDepartmentPropertyManagement = selectedDepartment === 'propertyManagement';
    
    const filtered = filterUsersByAreasAndServiceTypes(
      usersData.users,
      selectedAreas,
      selectedServiceTypes,
      stableAvailableServiceTypes,
      isHousekeeperManager,
      isPropertyManager,
      isDepartmentHousekeeping,
      isDepartmentPropertyManagement
    );
    
    return filtered;
  }, [usersData?.users?.length, selectedAreas.join(','), selectedServiceTypes.join(','), stableAvailableServiceTypes?.length, isHousekeeperManager, isPropertyManager, selectedDepartment]);

  const userFilteringInfo = useMemo(() => {
    if (!usersData?.users) return { totalUsers: 0, filteredUsers: 0, isFiltered: false };
    
    const totalUsers = usersData.users.length;
    
    // Determine department mode
    const isDepartmentHousekeeping = selectedDepartment === 'housekeeping';
    const isDepartmentPropertyManagement = selectedDepartment === 'propertyManagement';
    
    const filteredUsersArray = filterUsersByAreasAndServiceTypes(
      usersData.users,
      selectedAreas,
      selectedServiceTypes,
      stableAvailableServiceTypes,
      isHousekeeperManager,
      isPropertyManager,
      isDepartmentHousekeeping,
      isDepartmentPropertyManagement
    );
    
    return {
      totalUsers,
      filteredUsers: filteredUsersArray.length,
      isFiltered: filteredUsersArray.length < totalUsers
    };
  }, [usersData?.users?.length, selectedAreas.join(','), selectedServiceTypes.join(','), stableAvailableServiceTypes?.length, isHousekeeperManager, isPropertyManager, selectedDepartment]);

  const unassignedJobs = useMemo(() => {
    if (useInfiniteScrollUnassigned) {
      return infiniteScrollJobs.unassignedJobs;
    } else {
      if (!jobsData?.jobsForAssignments?.items) return [];
      // Filter out jobs that are assigned to either an individual user or a team
      return jobsData.jobsForAssignments.items.filter(job => 
        !job.assignedTo?.id && !job.assignedTeamId
      );
    }
  }, [
    useInfiniteScrollUnassigned,
    infiniteScrollJobs.unassignedJobs,
    jobsData?.jobsForAssignments?.items
  ]);

  // 🎯 CRITICAL FIX: Comprehensive cache invalidation function for date changes
  const invalidateAllAssignmentCaches = useCallback(() => {
    invalidateAssignmentCaches(client);
  }, [client]);

  // 🎯 ENHANCED: More thorough refetchJobsWithParams with comprehensive cache clearing
  const refetchJobsWithParams = useCallback((customDates = null) => {
    const dates = customDates || { startDate, endDate };
    
    // 🎯 CRITICAL: Invalidate all caches before refetching
    invalidateAllAssignmentCaches();
    
    // 🎯 NEW: Format dates consistently to prevent timezone issues
    const formattedStartDate = dates.startDate?.includes('T') ? dates.startDate : `${dates.startDate}T00:00:00.000Z`;
    const formattedEndDate = dates.endDate?.includes('T') ? dates.endDate : `${dates.endDate}T23:59:59.999Z`;
    
    // Update query variables for main jobs query
    const newVariables = {
      input: {
        ...debouncedQueryVariables.input,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      }
    };
    
    // Update query variables for assigned jobs query
    const newAssignedVariables = {
      input: {
        ...assignedJobsQueryVariables.input,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      }
    };
    
    // Refetch both queries with updated variables
    const mainRefetchPromise = useInfiniteScrollUnassigned 
      ? infiniteScrollJobs.refetchAll() 
      : refetchJobs(newVariables);
    const assignedRefetchPromise = refetchAssignedJobs(newAssignedVariables);
    
    // Update local state
    setStartDate(dates.startDate);
    setEndDate(dates.endDate);
    
    // Clear pagination state
    setDisplayLimit(DISPLAY_LIMITS.JOBS);
    setAllJobsLoaded(false);
  }, [startDate, endDate, debouncedQueryVariables, assignedJobsQueryVariables, refetchJobs, refetchAssignedJobs, invalidateAllAssignmentCaches]);

  const handleServiceTypeChange = useCallback((serviceTypes) => {
    setSelectedServiceTypes(serviceTypes);
    setHasUserInteractedWithServiceTypes(true);
  }, []);

  const handleDateRangeChange = useCallback((field, value) => {
    if (field === 'startDate') {
      setStartDate(value);
      
      // 🎯 NEW: Sync selectedDate when startDate changes in single day mode
      if (value === endDate) {
        const newDate = new Date(value + 'T12:00:00');
        setSelectedDate(newDate);
      }
    } else if (field === 'endDate') {
      setEndDate(value);
      
      // 🎯 NEW: Sync selectedDate when endDate changes in single day mode
      if (value === startDate) {
        const newDate = new Date(value + 'T12:00:00');
        setSelectedDate(newDate);
      }
    }
  }, [startDate, endDate]);

  const handlePropertySelect = useCallback((propertyId) => {
    setFilters(prev => {
      // Toggle property selection - if already selected, remove it; otherwise add it
      const currentPropertyIds = prev.propertyIds || [];
      const isAlreadySelected = currentPropertyIds.includes(propertyId);
      
      return {
        ...prev,
        propertyIds: isAlreadySelected 
          ? currentPropertyIds.filter(id => id !== propertyId)
          : [...currentPropertyIds, propertyId]
      };
    });
    
    // Clear pagination state
    setDisplayLimit(DISPLAY_LIMITS.JOBS);
    setAllJobsLoaded(false);
  }, []);

  const handleClearPropertyFilters = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      propertyIds: []
    }));
    setSelectedAreas([]);
    
    // Clear pagination state
    setDisplayLimit(DISPLAY_LIMITS.JOBS);
    setAllJobsLoaded(false);
  }, []);

  const handleAreaChange = useCallback((areas, userInitiated = false) => {
    setSelectedAreas(areas);
    if (userInitiated) {
      setHasUserInteractedWithAreas(true);
    }
  }, []);

  // 🎯 PERFORMANCE OPTIMIZATION: Enhanced loadMoreJobs with progressive loading
  const loadMoreJobs = useCallback((limit = PERFORMANCE_LIMITS.PROGRESSIVE_LOAD) => {
    if (useInfiniteScrollUnassigned) {
      // Use infinite scroll load more method
      infiniteScrollJobs.handleLoadMore(limit);
    } else {
      // Use traditional load more method
      if (jobsLoading || allJobsLoaded || !debouncedQueryVariables || isProgressiveLoading) {
        return;
      }
      
      setIsLoadingMore(true);
      
      const currentOffset = jobsData?.jobsForAssignments?.items?.length || 0;
      
      // Use performance-optimized limit
      const adjustedLimit = Math.max(limit, PERFORMANCE_LIMITS.PROGRESSIVE_LOAD);
      
      fetchMore({
        variables: {
          ...debouncedQueryVariables,
          input: {
            ...debouncedQueryVariables.input,
            offset: currentOffset,
            limit: adjustedLimit
          }
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          setIsLoadingMore(false);
          
          if (!fetchMoreResult?.jobsForAssignments?.items?.length) {
            setAllJobsLoaded(true);
            return prev;
          }
          
          const newJobs = fetchMoreResult.jobsForAssignments.items;
          const existingJobs = prev.jobsForAssignments?.items || [];
          
          // Filter out duplicates
          const uniqueNewJobs = newJobs.filter(newJob => 
            !existingJobs.some(existingJob => existingJob.id === newJob.id)
          );
          
          if (uniqueNewJobs.length === 0) {
            setAllJobsLoaded(true);
            return prev;
          }
          
          // Update display limit
          setDisplayLimit(prev => prev + uniqueNewJobs.length);
          
          // Reset error state on successful load
          setQueryRetryCount(0);
          setHasQueryError(false);
          setLastSuccessfulLoad(Date.now());
          
          return {
            ...prev,
            jobsForAssignments: {
              ...prev.jobsForAssignments,
              items: [...existingJobs, ...uniqueNewJobs],
              total: fetchMoreResult.jobsForAssignments.total,
              hasMore: fetchMoreResult.jobsForAssignments.hasMore
            }
          };
        }
      }).catch(error => {
        // Only log non-cancellation errors
        if (error.name !== 'AbortError' && !error.message.includes('cancelled')) {
          simpleErrorHandler(error, 'load more jobs');
        }
        setIsLoadingMore(false);
      });
    }
  }, [
    useInfiniteScrollUnassigned,
    infiniteScrollJobs.handleLoadMore,
    jobsLoading, 
    allJobsLoaded, 
    jobsData, 
    debouncedQueryVariables, 
    fetchMore, 
    isProgressiveLoading, 
    simpleErrorHandler
  ]);

  // 🎯 PERFORMANCE OPTIMIZATION: Disable auto-loading - let user scroll to trigger
  // Remove auto-loading effects that are causing double-loading issues

  // 🎯 CRITICAL FIX: Enhanced handleDateChange with proper cache invalidation and loading states
  const handleDateChange = useCallback((direction) => {
    // 🎯 NEW: Check if we're in date range mode and disable navigation
    const isDateRangeMode = startDate !== endDate;
    if (isDateRangeMode) {
      // Show a toast notification explaining why navigation is disabled
      toast.error('Day navigation is disabled when using a date range. Please use the date inputs to change dates.');
      return;
    }
    
    // 🎯 NEW: Set loading state for date transition
    setIsDateTransitioning(true);
    
    const newDate = new Date(selectedDate);
    if (direction === 'next' || direction === 1) {
      newDate.setDate(newDate.getDate() + 1);
    } else if (direction === 'prev' || direction === -1) {
      newDate.setDate(newDate.getDate() - 1);
    }
    
    // Update selected date
    setSelectedDate(newDate);
    
    // Update start and end date to match the selected date
    const formattedDate = format(newDate, 'yyyy-MM-dd');
    setStartDate(formattedDate);
    setEndDate(formattedDate);
    
    // Reset display limit to show initial page
    setDisplayLimit(DISPLAY_LIMITS.JOBS);
    setAllJobsLoaded(false);
    
    // 🎯 CRITICAL: Use date-specific cache invalidation
    invalidateAssignmentCachesForDateChange(client, formattedDate);
    
    // 🎯 CRITICAL: Pass the new dates directly to refetch with cache invalidation
    refetchJobsWithParams({
      startDate: formattedDate,
      endDate: formattedDate
    });
    
    // 🎯 NEW: Clear loading state after a short delay to allow cache invalidation to complete
    const timeoutId4 = setTimeout(() => {
      setIsDateTransitioning(false);
    }, 500);
    
    // Store timeout ID for cleanup
    return () => clearTimeout(timeoutId4);
  }, [selectedDate, startDate, endDate, refetchJobsWithParams, client]);

  // Update handleFilterChange to include endDate and handle property selection
  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    
    // Use the simplified refetch function for any filter changes
    if (name === 'department' || name === 'serviceType') {
      refetchJobsWithParams();
    }
  }, [refetchJobsWithParams]);

  // Effects - Add initialization guard to prevent multiple runs
  useEffect(() => {
    if (hasInitialized || serviceTypesLoading || !user) return;
    
    // Skip department-specific initialization if user has "show all departments" enabled
    // The useServiceTypes hook will handle auto-selecting all departments
    if (user?.showAllDepartmentsByDefault) {
      // Set department to null to indicate "all departments" mode
      if (selectedDepartment !== null) {
        setSelectedDepartment(null);
      }
      
      // Only auto-select user's assigned areas if not already set
      if (selectedAreas.length === 0 && user.areas.length > 0) {
        setSelectedAreas(user.areas);
      }
      setHasInitialized(true);
      return;
    }
    
    if ((isHousekeeperManager || isPropertyManager) && user?.areas && !hasUserInteractedWithAreas) {
      // Auto-select appropriate department using the departments field on service types
      if (!selectedDepartment) {
        if (isHousekeeperManager) {
          setSelectedDepartment('housekeeping');
          
          if (availableServiceTypes.length > 0) {
            const housekeepingServiceTypes = availableServiceTypes
              .filter(st => st.departments?.includes('HOUSEKEEPING'))
              .map(st => st.id);
            
            if (housekeepingServiceTypes.length > 0) {
              handleServiceTypeChange(housekeepingServiceTypes);
            }
          }
        } else if (isPropertyManager) {
          setSelectedDepartment('propertyManagement');
          
          if (availableServiceTypes.length > 0) {
            const propertyManagementServiceTypes = availableServiceTypes
              .filter(st => st.departments?.includes('PROPERTY_MANAGEMENT'))
              .map(st => st.id);
            
            if (propertyManagementServiceTypes.length > 0) {
              handleServiceTypeChange(propertyManagementServiceTypes);
            }
          }
        }
      }
      
      // Auto-select user's assigned areas if not already set
      if (selectedAreas.length === 0 && user.areas.length > 0) {
        setSelectedAreas(user.areas);
      }
      
      setHasInitialized(true);
    }
  }, [user?.id, user?.showAllDepartmentsByDefault, isHousekeeperManager, isPropertyManager, hasUserInteractedWithAreas, selectedDepartment, availableServiceTypes?.length, serviceTypesLoading, hasInitialized]);
  
  // Update jobCounts when data is loaded - use backend totals
  useEffect(() => {
    if (jobsData?.jobsForAssignments) {
      // Always use backend counts - they are the authoritative source
      const backendTotal = jobsData.jobsForAssignments.total || 0;
      const backendAssigned = jobsData.jobsForAssignments.assigned || 0;
      const backendUnassigned = jobsData.jobsForAssignments.unassigned || 0;
      
      setJobCounts({
        total: backendTotal,
        assigned: backendAssigned,
        unassigned: backendUnassigned
      });
    }
  }, [jobsData]);

  // Remove this broken effect that reduces displayLimit when jobs are assigned
  // The displayLimit should only increase when more jobs are loaded, never decrease
  // when jobs are assigned, otherwise pagination breaks

  // Check if all essential data has loaded at least once
  const hasEssentialData = useMemo(() => {
    const authReady = !authLoading && user !== undefined;
    const serviceTypesReady = !serviceTypesLoading && availableServiceTypes !== undefined;
    
    // Use appropriate loading states based on infinite scroll flag
    const jobsReady = useInfiniteScrollUnassigned 
      ? (!infiniteScrollJobs.loading && (infiniteScrollJobs.allJobs !== undefined || infiniteScrollJobs.error))
      : (!jobsLoading && (jobsData !== undefined || jobsError));
    
    const assignedJobsReady = !assignedJobsLoading && (assignedJobsData !== undefined || assignedJobsError);
    const usersReady = !usersLoading && (usersData !== undefined || usersError);
    
    return authReady && serviceTypesReady && jobsReady && assignedJobsReady && usersReady;
  }, [
    authLoading, user, 
    serviceTypesLoading, availableServiceTypes,
    useInfiniteScrollUnassigned,
    infiniteScrollJobs.loading, infiniteScrollJobs.allJobs, infiniteScrollJobs.error,
    jobsLoading, jobsData, jobsError,
    assignedJobsLoading, assignedJobsData, assignedJobsError,
    usersLoading, usersData, usersError
  ]);

  // Control when to show the page content
  useEffect(() => {
    
    if (hasEssentialData && !isInitialLoadComplete) {
      const timeElapsed = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, MINIMUM_LOADING_TIME - timeElapsed);
            
      if (remainingTime > 0) {
        // Wait for minimum loading time to prevent flashing
        const timer = setTimeout(() => {
          setIsInitialLoadComplete(true);
        }, remainingTime);
        
        return () => clearTimeout(timer);
      } else {
        setIsInitialLoadComplete(true);
      }
    }
  }, [hasEssentialData, isInitialLoadComplete, loadingStartTime, MINIMUM_LOADING_TIME]);

  // Emergency fallback to prevent infinite loading
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      if (!isInitialLoadComplete) {
        setIsInitialLoadComplete(true);
      }
    }, 10000); // 10 second fallback
    
    return () => clearTimeout(fallbackTimer);
  }, [isInitialLoadComplete]);





  // 🎯 PERFORMANCE OPTIMIZATION: Enhanced loading states with error recovery
  if (!isInitialLoadComplete || isDateTransitioning) {
    return (
      <div className="flex flex-col justify-center items-center h-full py-12">
        <Spinner size="lg" />
        <p className="mt-4 text-sm text-gray-100 lg:text-gray-600">
          {isDateTransitioning ? 'Loading new date...' : 'Loading assignments...'}
        </p>
        {hasQueryError && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              Some data is taking longer to load. The page will continue loading what's available.
            </p>
          </div>
        )}
      </div>
    );
  }

  // 🎯 NEW: Performance monitoring component
  const PerformanceMonitor = () => {
    if (!hasQueryError && !isProgressiveLoading && !isLoadingMore) {
      return null;
    }

    return (
      <div className="fixed bottom-4 right-4 z-40 bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-sm">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
          <span className="text-sm font-medium text-gray-700">Performance Mode</span>
        </div>
        {hasQueryError && (
          <p className="text-xs text-yellow-600 mt-1">
            Using reduced data limits for better performance
          </p>
        )}
        {isProgressiveLoading && (
          <p className="text-xs text-blue-600 mt-1">
            Loading data progressively...
          </p>
        )}
        {isLoadingMore && (
          <p className="text-xs text-green-600 mt-1">
            Loading more jobs...
          </p>
        )}
      </div>
    );
  };

  // Add this function to improve the debugging and handling of job creation
  const checkAndFixJobData = (job) => {
    if (!job) return null;
    
    // If job has propertyId but missing property object
    if (job.propertyId && (!job.property || !job.property.name)) {
      // Try to find the property from existing jobs
      const existingJobs = jobsData?.jobsForAssignments?.items || [];
      const matchingProperty = existingJobs.find(j => j.property?.id === job.propertyId)?.property;
      
      if (matchingProperty) {
        return {
          ...job,
          property: matchingProperty
        };
      }
      
      // No need to look up in propertiesData since we don't have it
    }
    
    return job;
  };
  
  // Improved job creation function with better cache management
  const handleAddNewJob = async (newJob) => {
    if (!newJob || !jobsData?.jobsForAssignments?.items) {
      return;
    }
    
    if (!newJob.property || !newJob.property.id) {
      // If we have propertyId but not property object, try to find the property from existing data
      if (newJob.propertyId) {
        const existingProperty = jobsData.jobsForAssignments.items
          .find(job => job.property?.id === newJob.propertyId)?.property;
          
        if (existingProperty) {
          newJob.property = existingProperty;
        } else {
          // If we can't find property info, request fresh data
          refetchJobs();
          return;
        }
      } else {
        refetchJobs();
        return;
      }
    }
    
    // Ensure the job object has all fields required by the cache schema
    const completeJob = {
      __typename: "Job",
      id: newJob.id || `temp-${Date.now()}`, // Temporary ID if none provided
      ...newJob,
      cleaningType: newJob.cleaningType || (newJob.category === 'CLEANING' ? 'STANDARD' : null),
      isOwnerArrival: newJob.isOwnerArrival || false,
      isOwnerDeparture: newJob.isOwnerDeparture || false,
      reservationId: newJob.reservationId || null,
      reservation: newJob.reservation || null,
      assignedTo: newJob.assignedTo || null,
      serviceType: newJob.serviceType || null,
      statusHistory: newJob.statusHistory || [],
      isCompleted: newJob.isCompleted || false,
      createdAt: newJob.createdAt || new Date().toISOString(),
      updatedAt: newJob.updatedAt || new Date().toISOString()
    };

    // Ensure property has required fields
    if (completeJob.property) {
      completeJob.property = {
        __typename: "Property",
        ...completeJob.property,
        checkInTime: completeJob.property.checkInTime || '15:00',
        city: completeJob.property.city || '',
        state: completeJob.property.state || '',
        zip: completeJob.property.zip || '',
        propertyCode: completeJob.property.propertyCode || ''
      };
    }
    
    try {
      // Read current cache data
      const existingData = client.readQuery({
        query: GET_JOBS_FOR_ASSIGNMENTS,
        variables: {
          input: {
            startDate: formatInTimeZone(
              new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()),
              'America/New_York',
              'yyyy-MM-dd\'T\'00:00:00.000XXX'
            ),
            endDate: formatInTimeZone(
              new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59),
              'America/New_York',
              'yyyy-MM-dd\'T\'23:59:59.999XXX'
            ),
            limit: API_PAGE_LIMITS.JOBS,
            offset: 0
            }
          }
        });
      
      if (!existingData?.jobsForAssignments) {
        // If no cache data, fall back to refetch
        await refetchJobs();
        return;
      }
      
      // Create a new array with the new complete job
      const updatedItems = [...existingData.jobsForAssignments.items, completeJob];
      
      // Update the cache directly
      client.writeQuery({
        query: GET_JOBS_FOR_ASSIGNMENTS,
        variables: {
          input: {
            startDate: formatInTimeZone(
              new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()),
              'America/New_York',
              'yyyy-MM-dd\'T\'00:00:00.000XXX'
            ),
            endDate: formatInTimeZone(
              new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59),
              'America/New_York',
              'yyyy-MM-dd\'T\'23:59:59.999XXX'
            ),
            limit: API_PAGE_LIMITS.JOBS,
            offset: 0
          }
        },
        data: {
          jobsForAssignments: {
            ...existingData.jobsForAssignments,
            items: updatedItems,
            total: (existingData.jobsForAssignments.total || 0) + 1,
            unassigned: (existingData.jobsForAssignments.unassigned || 0) + 1, // New job is unassigned
          }
        }
      });
      
      // Update job counts
    setJobCounts(prev => ({
      ...prev,
      unassigned: prev.unassigned + 1,
      total: prev.total + 1
    }));
      
      // Show success notification
      showNotification(`New job created for ${completeJob.property?.name || 'property'}`, 'success');
    } catch (e) {
      console.error('Error updating cache with new job:', e);
      // Fall back to refetch if cache update fails
      await refetchJobs();
    }
  };
  
  // Handler for copying a job
  const handleCopyJob = (copyData) => {
    setCopyJobData(copyData);
    setIsAddJobModalOpen(true);
  };

  // Override the handleAddNewJob function to use this check
  const addNewJobWithCheck = (newJob) => {
    // Use imported utility function
    handleAddNewJobWithCheck(
      newJob, 
      jobsData, 
      null, // remove propertiesData - will be handled in the component
      jobCounts,
      totalJobsCount, 
      client, 
      selectedDate, 
      showNotification, 
      refetchJobsWithParams, 
      setJobCounts
    );
  };

  // Function to handle job assignment
  const handleAssignJobs = () => {
    if (!selectedUserId || selectedJobs.length === 0) {
      toast.error('Please select a user and at least one job');
      return;
    }    
    // Call the mutation to assign the jobs
    assignJobs({
      variables: {
        userId: selectedUserId,
        jobIds: selectedJobs
      }
    });
  };

  // Function to handle team job assignment
  const handleAssignJobsToTeam = (jobIds, teamId) => {
    if (!jobIds || jobIds.length === 0) {
      toast.error('Please select at least one job');
      return;
    }

    // Validate teamId - must be a non-empty value that can be parsed as an integer
    const parsedTeamId = teamId ? parseInt(teamId, 10) : null;
    if (!parsedTeamId || isNaN(parsedTeamId)) {
      toast.error('Please select a valid team');
      return;
    }

    // Call the mutation to assign jobs to team
    assignJobsToTeam({
      variables: {
        teamId: parsedTeamId,
        jobIds: jobIds
      }
    });
  };

  // 🎯 NEW: Handle bulk date update
  const handleBulkDateUpdate = async (jobIds, newDate) => {
    if (!jobIds || jobIds.length === 0 || !newDate) {
      toast.error('Please select jobs and a date');
      return;
    }

    try {
      // Fix timezone issue: Parse date as local date to avoid timezone conversion
      const [year, month, day] = newDate.split('-').map(Number);
      const localDate = new Date(year, month - 1, day, 12, 0, 0); // Set to noon local time
      
      await bulkUpdateJobDates({
        variables: {
          input: {
            jobIds: jobIds,
            newDate: localDate,
            hasDateOverride: true,
            reason: `Bulk date change to ${newDate} via Assignments page`
          }
        }
      });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  // 🎯 NEW: Handle combined bulk update and assign
  const handleBulkUpdateAndAssign = async (jobIds, newDate, assignToUserId, assignToTeamId) => {
    if (!jobIds || jobIds.length === 0) {
      toast.error('Please select jobs');
      return;
    }

    if (!newDate && !assignToUserId && !assignToTeamId) {
      toast.error('Please specify a date change or assignment');
      return;
    }

    try {
      const input = {
        jobIds: jobIds,
        hasDateOverride: true,
        reason: `Combined bulk operation via Assignments page`
      };

      if (newDate) {
        // Fix timezone issue: Parse date as local date to avoid timezone conversion
        const [year, month, day] = newDate.split('-').map(Number);
        input.newDate = new Date(year, month - 1, day, 12, 0, 0); // Set to noon local time
      }

      if (assignToUserId) {
        input.assignToUserId = parseInt(assignToUserId);
      } else if (assignToTeamId) {
        input.assignToTeamId = parseInt(assignToTeamId);
      }

      await bulkUpdateAndAssignJobs({
        variables: { input }
      });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  // Filter users based on areas, service types, and role restrictions
  // (filteredUsers is now defined in the hooks section above)

  // Get filtering information for display
  // (userFilteringInfo is now defined in the hooks section above)

  // Get unassigned jobs - moved to here before any useEffect can reference it
  // (unassignedJobs is now defined in the hooks section above)

  // Function to load more unassigned jobs - convert to useCallback to avoid circular dependencies
  // (loadMoreJobs is now defined in the hooks section above)

  // 🎯 PERFORMANCE OPTIMIZATION: Enhanced error handling with graceful degradation
  if (jobsError || assignedJobsError || usersError) {
    const hasAnyData = jobsData || assignedJobsData || usersData;
    
    if (!hasAnyData) {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-red-500 text-center">
            <p className="text-lg font-medium mb-2">Unable to load assignments data</p>
            <p className="text-sm text-gray-600 mb-4">
              This might be due to a server timeout or network issue.
            </p>
            <button 
              onClick={() => {
                setQueryRetryCount(0);
                setHasQueryError(false);
                if (typeof refetchJobsWithReducedLimits === 'function') {
                  refetchJobsWithReducedLimits();
                } else {
                  window.location.reload();
                }
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Try Again with Reduced Limits
            </button>
          </div>
        </div>
      );
    }
    
    // If we have some data, show a warning but continue
    showNotification('Some data failed to load completely. Showing available data.', 'warning');
  }

  return (
    <div className="space-y-6">
      {/* Role-based service type filter notification */}
      {filterInfo?.isFiltered && filterInfo?.hasRoleBasedRestriction && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <span className="font-medium">Smart Filter Active:</span> Showing service types for your role ({filterInfo.totalAvailable} of {filterInfo.totalInSystem} types). You can toggle this in the service type filter.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Use the new AssignmentsHeader component */}
      <AssignmentsHeader
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        filters={filters}
        setFilters={setFilters}
        propertySearch={propertySearch}
        setPropertySearch={setPropertySearch}
        showPropertyDropdown={showPropertyDropdown}
        setShowPropertyDropdown={setShowPropertyDropdown}
        handleDateChange={handleDateChange}
        handleFilterChange={handleFilterChange}
        handlePropertySelect={handlePropertySelect}
        handleClearPropertyFilters={handleClearPropertyFilters}
        isHousekeeperManager={isHousekeeperManager}
        isPropertyManager={isPropertyManager}
        onAddJob={() => setIsAddJobModalOpen(true)}
        startDate={startDate}
        endDate={endDate}
        handleDateRangeChange={handleDateRangeChange}
        selectedAreas={selectedAreas}
        handleAreaChange={handleAreaChange}
        hasUserInteractedWithAreas={hasUserInteractedWithAreas}
        selectedServiceTypes={selectedServiceTypes}
        handleServiceTypeChange={handleServiceTypeChange}
        availableServiceTypes={stableAvailableServiceTypes}
        isRoleFilterEnabled={isRoleFilterEnabled}
        onRoleFilterToggle={setRoleFilterEnabled}
        filterInfo={filterInfo}
        serviceTypesLoading={serviceTypesLoading}
        userRole={user?.role}
        isFiltersOpen={isFiltersOpen}
        setIsFiltersOpen={setIsFiltersOpen}
        columnLayout={columnLayout}
        setColumnLayout={setColumnLayout}
        selectedDepartment={selectedDepartment}
        setSelectedDepartment={setSelectedDepartment}
                    useOptimizedAssignedTasks={useOptimizedAssignedTasks}
      />
      
      {/* Dynamic grid layout based on column toggle */}
      <div className={`grid gap-6 ${
        columnLayout === 1 
          ? 'grid-cols-1' 
          : 'grid-cols-1 lg:grid-cols-2'
      }`}>
        {/* Assigned Tasks Component */}
        {useOptimizedAssignedTasks ? (
          <AssignedTasksOptimized 
            queryVariables={debouncedQueryVariables}
            totalJobsCount={totalJobsCount} 
            setEditingJob={setEditingJob}
            useOptimizedLoading={true}
            clearCacheTrigger={assignedTasksCacheTrigger}
          />
        ) : (
          <AssignedTasks 
            groupedAssignedJobs={groupedAssignedJobs} 
            totalJobsCount={totalJobsCount} 
            setEditingJob={setEditingJob} 
          />
        )}
        
        {/* Unassigned Tasks Component - with infinite scroll option */}
        {useInfiniteScrollUnassigned ? (
          <UnassignedTasksInfiniteScroll 
            unassignedJobs={unassignedJobs}
            totalJobsCount={totalJobsCount}
            hasMoreJobs={infiniteScrollJobs.hasMore}
            isLoadingMoreJobs={infiniteScrollJobs.isLoadingMore}
            selectedJobs={selectedJobs}
            setSelectedJobs={setSelectedJobs}
            selectedUserId={selectedUserId}
            setSelectedUserId={setSelectedUserId}
            filteredUsers={filteredUsers}
            userFilteringInfo={userFilteringInfo}
            handleAssignJobs={handleAssignJobs}
            handleAssignJobsToTeam={handleAssignJobsToTeam}
            assignLoading={assignLoading || assignToTeamLoading}
            setEditingJob={setEditingJob}
            isHousekeeperManager={isHousekeeperManager}
            isPropertyManager={isPropertyManager}
            loadMoreJobs={infiniteScrollJobs.handleLoadMore}
            startDate={startDate}
            endDate={endDate}
            // Pass loading states to show proper loading indicators
            jobsLoading={infiniteScrollJobs.loading}
            assignedJobsLoading={assignedJobsLoading}
            usersLoading={usersLoading}
            // Pass bulk date update props
            onBulkDateUpdate={handleBulkDateUpdate}
            bulkDateUpdateLoading={bulkDateUpdateLoading}
            // Pass combined bulk update and assign props
            onBulkUpdateAndAssign={handleBulkUpdateAndAssign}
            bulkUpdateAndAssignLoading={bulkUpdateAndAssignLoading}
            // Optional container ref for backward compatibility
            unassignedJobsContainerRef={unassignedJobsContainerRef}
          />
        ) : (
          <UnassignedTasks 
            unassignedJobs={unassignedJobs}
            totalJobsCount={totalJobsCount}
            selectedJobs={selectedJobs}
            setSelectedJobs={setSelectedJobs}
            selectedUserId={selectedUserId}
            setSelectedUserId={setSelectedUserId}
            filteredUsers={filteredUsers}
            userFilteringInfo={userFilteringInfo}
            handleAssignJobs={handleAssignJobs}
            handleAssignJobsToTeam={handleAssignJobsToTeam}
            assignLoading={assignLoading || assignToTeamLoading}
            setEditingJob={setEditingJob}
            isHousekeeperManager={isHousekeeperManager}
            isPropertyManager={isPropertyManager}
            unassignedJobsContainerRef={unassignedJobsContainerRef}
            loadMoreJobs={loadMoreJobs}
            hasMoreJobs={jobsData?.jobsForAssignments?.hasMore || false}
            isLoadingMoreJobs={isLoadingMore}
            startDate={startDate}
            endDate={endDate}
            // Pass loading states to show proper loading indicators
            jobsLoading={jobsLoading}
            assignedJobsLoading={assignedJobsLoading}
            usersLoading={usersLoading}
            // Pass bulk date update props
            onBulkDateUpdate={handleBulkDateUpdate}
            bulkDateUpdateLoading={bulkDateUpdateLoading}
            // Pass combined bulk update and assign props
            onBulkUpdateAndAssign={handleBulkUpdateAndAssign}
            bulkUpdateAndAssignLoading={bulkUpdateAndAssignLoading}
          />
        )}
      </div>

      {editingJob && (
        <EditJobModal
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onUpdate={(updatedJob) => {
            setEditingJob(null);
            
            // Trigger assigned tasks cache clearing
            setAssignedTasksCacheTrigger(prev => prev + 1);
            
            // Force refetch to get updated data
            refetchJobsWithParams();
          }}
          onCopyJob={handleCopyJob}
        />
      )}

      {/* Add Job Modal */}
      <AddJobModal
        isOpen={isAddJobModalOpen}
        onClose={() => {
          setIsAddJobModalOpen(false);
          setCopyJobData(null); // Clear copy data when modal closes
        }}
        onCreateJob={(newJob) => {
          addNewJobWithCheck(newJob);
          setCopyJobData(null); // Clear copy data after successful creation
        }}
        initialPropertyId={copyJobData?.propertyId}
        initialProperty={copyJobData?.property}
        initialNotes={copyJobData?.notes}
        initialScheduledDate={copyJobData?.scheduledDate}
        initialPayout={copyJobData?.payout}
        initialBilledAmount={copyJobData?.billedAmount}
      />

      {renderNotification()}
      
      {/* 🎯 NEW: Performance monitoring component */}
      <PerformanceMonitor />
    </div>
  );
}